import { classNames } from "@circle-react/helpers/twMergeWithCN";
import {
  Children,
  cloneElement,
  isValidElement,
  type ComponentPropsWithoutRef,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from "react";

import {
  type InactiveSidebarNavLinkHoverScope,
  useSidebarNavLinkClass,
} from "../useSidebarNavLinkClass";
import { Content, type SidebarItemContentProps } from "./Content";

const BASE_STYLES =
  "flex w-full min-w-0 items-center gap-2 h-9 px-3 rounded-md text-start text-sm font-medium leading-5 no-underline transition-colors duration-200 group/sidebar-item";

export interface SidebarItemRootProps extends Omit<
  ComponentPropsWithoutRef<"button">,
  "children"
> {
  children: ReactElement<SidebarItemContentProps>;
  hasUnreadContent?: boolean;
  hoverScope?: InactiveSidebarNavLinkHoverScope;
  isActive?: boolean;
  isDisabled?: boolean;
  rootElement?: ReactElement;
}

interface RootElementProps {
  children?: React.ReactNode;
  className?: string;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
}

const getContentProps = (children: ReactNode): SidebarItemContentProps => {
  const child = Children.only(children);

  if (
    !isValidElement<SidebarItemContentProps>(child) ||
    child.type !== Content
  ) {
    throw new Error("SidebarItem must have a SidebarItem.Content child");
  }

  return child.props;
};

export const Root = ({
  children,
  className,
  hasUnreadContent = false,
  isActive = false,
  isDisabled = false,
  onClick,
  rootElement,
  hoverScope = "direct",
  ...buttonProps
}: SidebarItemRootProps) => {
  const { label } = getContentProps(children);
  const { getActiveInactiveClasses, getUnreadClasses } =
    useSidebarNavLinkClass();

  const mergedContainerClassName = classNames(
    BASE_STYLES,
    getActiveInactiveClasses(isActive, hoverScope),
    getUnreadClasses(hasUnreadContent),
    className,
  );

  const handleClick = (
    event: MouseEvent<HTMLElement>,
    rootOnClick?: (event: MouseEvent<HTMLElement>) => void,
  ) => {
    if (isDisabled) {
      event.preventDefault();
      return;
    }

    onClick?.(event as MouseEvent<HTMLButtonElement>);
    rootOnClick?.(event);
  };

  if (rootElement && isValidElement(rootElement)) {
    const rootProps = rootElement.props as RootElementProps;

    return cloneElement(rootElement, {
      "aria-disabled": isDisabled,
      className: classNames(rootProps.className, mergedContainerClassName),
      title: label,
      children,
      onClick: (event: MouseEvent<HTMLElement>) => {
        handleClick(event, rootProps.onClick);
      },
    } as RootElementProps);
  }

  return (
    <button
      type="button"
      aria-disabled={isDisabled}
      className={mergedContainerClassName}
      disabled={isDisabled}
      title={label}
      {...buttonProps}
      onClick={handleClick}
    >
      {children}
    </button>
  );
};
